<template>
  <div class="datatable-container">
    <!-- Filters -->
    <div class="datatable-filters">
      <input type="date" class="datatable-input" />
      <input type="date" class="datatable-input" />
      <select class="datatable-select">
        <option value="all">All Engagement</option>
        <option value="engaged">Engaged</option>
        <option value="not-engaged">Not Engaged</option>
      </select>
      <select class="datatable-select">
        <option value="all">All Equity</option>
        <option value="1">Equity 1</option>
        <option value="2">Equity 2</option>
        <option value="3">Equity 3</option>
        <option value="4">Equity 4</option>
        <option value="5">Equity 5</option>
      </select>
      <select class="datatable-select">
        <option value="all">Results Per Page</option>
        <option value="10">10</option>
        <option value="20">20</option>
        <option value="50">50</option>
        <option value="100">100</option>
      </select>
      <button class="datatable-reset-btn">
        Reset
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="datatable-loading">
      <div class="datatable-spinner"></div>
      <p>Loading customers...</p>
    </div>

    <!-- Error State -->
    <div v-if="error" class="datatable-error">
      ❌ {{ error }}
    </div>

    <!-- Customer Table -->
    <div v-else-if="!loading" class="datatable-table-container">
      <RevoGrid
        :columns="columnsSimple"
        :source="cleanedCustomers"
        :rowSize="56"
        theme="material"
        class="revogrid-custom"
        :readonly="true"
        :rowClass="rowClass"
        :additionalData="additionalData"
        @pagechange="handlePageChange"
      />
      <div v-if="customers.length === 0" class="datatable-no-results">
        No customers found for the selected criteria.
      </div>
    </div>
    <!-- Pagination -->
    <div
      v-if="totalPages > 1"
      class="datatable-pagination"
    >
      <button
        @click="changePage(page - 1)"
        :disabled="page === 1"
        class="datatable-pagination-btn"
      >
        ← Previous
      </button>

      <span class="datatable-pagination-info">
        Page {{ page }} of {{ totalPages }}
        ({{ totalRecords }} total)
      </span>

      <button
        @click="changePage(page + 1)"
        :disabled="page >= totalPages"
        class="datatable-pagination-btn"
      >
        Next →
      </button>
    </div>
  </div>
</template>


<script setup lang="ts">

import { ref,watch,  computed } from 'vue'
//import PaginationPlugin from '@revolist/revogrid-pro';
//const plugins = [PaginationPlugin]

const additionalData = computed(() => ({
  pagination: {
    itemsPerPage: props.pageSize,
    initialPage: props.page - 1,
    total: props.totalRecords,
    buttonCount: 5,
  }
}))
const emit = defineEmits(['pagechange'])
function handlePageChange({ detail }: { detail: { skip?: number; count?: boolean } }) {
  // skip is the page index (zero-based)
  emit('pagechange', { page: (detail.skip ?? 0) + 1 })
}

import type { Customer } from '@/types/customer';
import RevoGrid, { VGridVueTemplate } from '@revolist/vue3-datagrid';
import type { AdminCustomerResponseDTO } from '@/models/Customers/AdminResponseDTO';
import DataTableCellVehicle from '@/components/SuperAdmin/DataTable.Cell.Vehicle.vue';
import DataTableCellCustomer from '@/components/SuperAdmin/DataTable.Cell.Customer.vue';
import CellEngagement from '@/components/SuperAdmin/Cell.Engagement.vue';
import CellEquityScore from '@/components/SuperAdmin/Cell.EquityScore.vue';

const props = defineProps<{
  customers: Array<AdminCustomerResponseDTO>
  loading: boolean
  error?: string
  selectedTenant?: string
  totalRecords: number
  pageSize: number
  page: number
}>()

const columnsSimple = [
  { 
    prop: 'customer', 
    name: 'Customer', 
    size: 160,
    cellTemplate: VGridVueTemplate(DataTableCellCustomer)  
  },
  { prop: 'appt_date', name: 'Appt. Date', size: 120 },
  { prop: 'appt_time', name: 'Appt. Time', size: 120 },
  { 
    prop: 'engaged', 
    name: 'Engaged', 
    size: 160,
    cellTemplate: VGridVueTemplate(CellEngagement)  
  },
  { 
    prop: 'equity_score', 
    name: 'Equity Score', 
    size: 160,
    cellTemplate: VGridVueTemplate(CellEquityScore)  
  },
  // { prop: 'deal_status', name: 'Deal Status', size: 120 },
  { 
    prop: 'vehicle', 
    name: 'Vehicle', 
    size: 160,
    cellTemplate: VGridVueTemplate(DataTableCellVehicle)  
  },
  { prop: 'credit_score', name: 'Credit Score', size: 120 },
  // { prop: 'credit_freeze', name: 'Credit Freeze', size: 110 },
  { prop: 'value', name: 'Valuation', size: 110 },
  { prop: 'loan_balance', name: 'Loan Balance', size: 120 },
  { prop: 'payment', name: 'Payment', size: 100 },
  { prop: 'acct_type', name: 'Account Type', size: 110 }
]


const cleanedCustomers = computed(() =>
  props.customers.map(c => {
    const cleaned: Record<string, any> = {}
    columnsSimple.forEach(col => {
      if (col.prop === 'customer') {
        cleaned['customer'] = {
          first_name: c.first_name,
          last_name: c.last_name,
          phone: c.phone
        }
      } else if (col.prop === 'vehicle') {
        // If you want to do something similar for vehicle, do it here
        cleaned['vehicle'] = {
          // ...vehicle fields from c
        }
      } else {
        cleaned[col.prop] = c[col.prop as keyof AdminCustomerResponseDTO] ?? ''
      }
    })
    return cleaned
  })
)

function rowClass(row: any) {
  return row.engaged ? 'row-engaged' : 'row-not-engaged'
}

const page = ref(1); // Start at page 1
const pageSize = ref(3);

const totalRecords = ref(2);
const totalPages = computed(() => {
  //return Math.ceil(totalRecords.value / pageSize.value);
  return 2;
});
const changePage = async (newPage: number) => {
    if (newPage < 1 || newPage > totalPages.value) return;
    
    page.value = newPage;
    //await reloadCustomers();
};
</script>

<style scoped>
.datatable-container {
  max-width: 100%;
  margin: 0 auto;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  padding: 32px 28px 40px 28px;
  font-family: 'Inter', Arial, sans-serif;
}

.datatable-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.datatable-title {
  font-size: 2rem;
  font-weight: bold;
  color: #22223b;
}

.datatable-header-actions {
  display: flex;
  gap: 12px;
}

.datatable-export-btn {
  background: #4f8cff;
  color: #fff;
  border: none;
  padding: 10px 22px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.datatable-export-btn:hover {
  background: #2563eb;
}

.datatable-filters {
  display: flex;
  gap: 16px;
  margin-bottom: 1.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.datatable-input,
.datatable-select {
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  background: #f9f9fb;
  color: #22223b;
  transition: border-color 0.2s;
  outline: none;
}
.datatable-input:focus,
.datatable-select:focus {
  border-color: #4f8cff;
}

.datatable-reset-btn {
  background: #f3f4f6;
  color: #22223b;
  border: 1px solid #e0e0e0;
  padding: 8px 18px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}
.datatable-reset-btn:hover {
  background: #e0e7ef;
  border-color: #4f8cff;
}

.datatable-loading,
.datatable-error {
  text-align: center;
  margin: 2.5rem 0;
  color: #6c6c80;
  font-size: 1.1rem;
}

.datatable-spinner {
  border: 4px solid #e0e0e0;
  border-top: 4px solid #4f8cff;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  margin: 0 auto 0.75rem auto;
  animation: datatable-spin 1s linear infinite;
}
@keyframes datatable-spin {
  0% { transform: rotate(0deg);}
  100% { transform: rotate(360deg);}
}

.datatable-table-container {
  border-radius: 12px;
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  background: #f9f9fb;
  max-width: 100%;
}

.datatable-no-results {
  padding: 2rem 0;
  color: #6c6c80;
  font-size: 1.1rem;
  text-align: center;
}

.datatable-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 18px;
  margin-top: 2rem;
}

.datatable-pagination-btn {
  background: #f3f4f6;
  color: #22223b;
  border: 1px solid #e0e0e0;
  padding: 8px 18px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}
.datatable-pagination-btn:disabled {
  background: #e5e7eb;
  color: #bdbdbd;
  cursor: not-allowed;
}
.datatable-pagination-btn:hover:not(:disabled) {
  background: #e0e7ef;
  border-color: #4f8cff;
}

.datatable-pagination-info {
  font-size: 1rem;
  color: #6c6c80;
}

/* Existing styles for revogrid and row classes remain */
.revogrid-custom {
  --rg-header-bg: #f3f4f6;
  --rg-header-font-color: #22223b;
  --rg-row-bg: #fff;
  --rg-row-hover-bg: #f3f4f6;
  --rg-border-color: #e5e7eb;
  --rg-font-family: 'Inter', Arial, sans-serif;
  --rg-font-size: 1rem;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  margin-bottom: 1rem;
  height: 500px; /* Increase this value as needed */
  min-height: 400px;
  max-height: 80vh;
}

.row-engaged {
  background: #d1fae5 !important;
}
.row-not-engaged {
  background: #fee2e2 !important;
}

</style>